import { ChoiceListSearch } from '@europrocurement/flexy-components/components/molecules/ChoiceListSearch/ChoiceListSearch';
import {
    PRESCRIPTEUR_SLICE_NAME,
    PrescripteursSelector,
    TiersAppDispatch,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { SearchBarRegistry } from '@europrocurement/l2d-modules/modules/Societes';
import { Box, Container } from '@mui/system';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Typography } from '@mui/material';
import {
    ActionButton,
    BoxSkeleton,
    ChoiceItem,
    ContinueButton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { addressToString } from '@europrocurement/l2d-utils';
import { openIcon, plusIcon } from '@europrocurement/l2d-icons';
import { useParcoursFormalite } from '../provider/useParcoursFormalite';
import { parcoursFormalitePathResolver } from '../parcoursFormalitePathResolver';
import SelectionContact from '../components/SelectionContact';

const StepPrescriber: React.FunctionComponent = function () {
    const dispatch = useDispatch<TiersAppDispatch>();
    const navigate = useNavigate();

    const datasource = useTiersServiceSelector(PrescripteursSelector).main;

    const { containerWidth, contact, setContact } = useParcoursFormalite();

    const { getPrescripteurs, selectPrescripteur } = useTiersService();

    const [searchValue, setSearchValue] = React.useState<string>('');

    const onSearchWrapper = useDebouncedCallback((currentSearchString: string) => {
        setSearchValue(currentSearchString);
        if (currentSearchString !== '') {
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/setmainSearch`,
                payload: {
                    search: currentSearchString,
                },
            });
            dispatch(getPrescripteurs({}));
        } else {
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainSearch`,
            });
            dispatch(getPrescripteurs({}));
        }
    }, 500);

    const searchWrapper: (searchString: string) => void = (searchString) => {
        onSearchWrapper(searchString);
        setSearchValue(searchString);
    };

    React.useEffect(() => {
        const resetStore = () => {
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/setmainPagination`,
                payload: { itemsPerPage: 5, page: 0 },
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainSearch`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainFilter`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainOrder`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/clearmainData`,
            });
        };
        resetStore();
        return () => {
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainPagination`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainSearch`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainFilter`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/resetmainOrder`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/clearmainData`,
            });
        };
    }, [dispatch]);

    return (
        <Container maxWidth={containerWidth}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                }}
            >
                <ChoiceListSearch
                    title="Sélection du prescripteur"
                    description=""
                    searchValue={searchValue}
                    setSearchValue={searchWrapper}
                    placeholderLabelSearch={SearchBarRegistry.prescriber?.placeholder || ''}
                />
                <SkeletonLoader
                    isLoading={datasource.selectedStatus === 'loading'}
                    skeleton={
                        <BoxSkeleton
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                width: 'calc(100% - 25px)',
                                height: '61px',
                                marginTop: '32px',
                            }}
                        />
                    }
                >
                    {datasource.selected ? (
                        <Box>
                            <Typography
                                sx={{
                                    paddingLeft: '15px',
                                    paddingBottom: '8px',
                                }}
                                variant="l2dcaption"
                                color="text.secondary"
                            >
                                Prescripteur Sélectionné
                            </Typography>
                            <ChoiceItem
                                label={datasource.selected.raisonSociale || ''}
                                idFiscal={datasource.selected.siren || ''}
                                description={addressToString(datasource.selected.adresses[0])}
                                isChecked
                                sx={{ width: 'calc(100% - 25px)' }}
                                value=""
                                isDisabled={false}
                                invisibleCheckbox
                            />{' '}
                        </Box>
                    ) : null}
                </SkeletonLoader>

                <Typography
                    sx={{
                        paddingLeft: '15px',
                        paddingBottom: '8px',
                    }}
                    variant="l2dcaption"
                    color="text.secondary"
                >
                    Liste des prescripteurs
                </Typography>

                <SkeletonLoader
                    isLoading={datasource.status === 'loading'}
                    // isLoading
                    skeleton={
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap="2px"
                        >
                            {['1', '2', '3', '4', '5'].map((innerIdx) => (
                                <BoxSkeleton
                                    key={`skeleton-${innerIdx}}`}
                                    sx={{
                                        width: 'calc(100% - 25px)',
                                        height: '61px',
                                    }}
                                />
                            ))}
                        </Box>
                    }
                >
                    {datasource.data.slice(0, 5).map((item) => (
                        <ChoiceItem
                            key={item.id}
                            value={item.id ? item.id.toString() : ''}
                            isDisabled={false}
                            invisibleCheckbox
                            sx={{ paddingRight: '25px' }}
                            label={item.raisonSociale || ''}
                            idFiscal={item.siren || ''}
                            description={addressToString(item.adresses[0])}
                            updateItem={() => {
                                navigate(`/prescripteurs/liste/${item.id}`);
                            }}
                            isChecked={
                                datasource.selected ? datasource.selected.id === item.id : false
                            }
                            updateIcon={openIcon}
                            onChange={(value) => {
                                if (item.contacts.length > 1) {
                                    setContact(item.contacts[0]);
                                } else {
                                    setContact(undefined);
                                }
                                dispatch(
                                    selectPrescripteur({
                                        id: Number(value),
                                    }),
                                );
                            }}
                        />
                    ))}
                </SkeletonLoader>
                <ActionButton
                    icon={plusIcon}
                    label="Ajouter un prescripteur"
                    action={() => {
                        navigate('/prescripteurs/new');
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 14px',
                        gap: '12px',
                        borderRadius: '12px',
                        width: '100%',
                        justifyContent: 'flex-start',
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        fontWeight: '600',
                        lineHeight: '24px',
                        color: 'text.secondary',
                        backgroundColor: 'inherit',
                        margin: '0',
                        transition: 'background-color ease-in-out 300ms',
                        '&:hover': {
                            backgroundColor: 'text.gris005',
                        },
                    }}
                />
                {datasource.selected ? (
                    <SelectionContact
                        contact={contact}
                        setContact={setContact}
                        prescripteur={datasource.selected}
                    />
                ) : null}
            </Box>
            <ContinueButton
                disabled={
                    contact === null ||
                    contact === undefined ||
                    datasource.selected === null ||
                    datasource.selected === undefined ||
                    datasource.selectedStatus === 'loading'
                }
                onClick={() => {
                    navigate(parcoursFormalitePathResolver('stepFormalite'));
                }}
            />
        </Container>
    );
};
export default StepPrescriber;
